import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { AdminCrudShell, StatusBadge } from '@/components/admin/AdminCrudShell';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ChevronRight, Search, Users, X } from 'lucide-react';
import { fetchAdminList, fetchSports, friendlyError } from '@/lib/api';
import { toast } from 'sonner';

const CATEGORY_OPTIONS = [
  'senior', 'juvenil', 'cadete', 'infantil', 'alevin', 'benjamin',
].map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));

const GENDER_OPTIONS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'femenino', label: 'Femenino' },
  { value: 'mixto', label: 'Mixto' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
];

const genderLabel = (g) => GENDER_OPTIONS.find((o) => o.value === g)?.label || '—';

// Acción por fila en la lista de equipos (extraída para evitar warning
// react/no-unstable-nested-components).
function TeamRowAction({ row }) {
  return (
    <Button
      asChild
      size="sm"
      variant="ghost"
      data-testid={`admin-teams-directory-${row.id}`}
      className="h-8 px-2 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15"
    >
      <Link
        to={`/admin/clubes/${row.club_id}/equipos/${row.id}/directorio`}
        className="inline-flex items-center gap-1"
      >
        <Users className="h-3.5 w-3.5 mr-0.5" /> Directorio
      </Link>
    </Button>
  );
}

/**
 * Admin → Equipos / Categorías
 *
 * Navegación en dos niveles reutilizando los endpoints existentes:
 *   1) Lista de clubes con filtro Deporte + búsqueda por nombre de club.
 *      Cada fila muestra: Club | Deporte | Nº equipos.
 *   2) Al seleccionar un club, drill-down al CRUD de sus equipos
 *      (reutiliza AdminCrudShell con extraQueryParams={ club_id }).
 */
export default function AdminTeams() {
  const [sports, setSports] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [teams, setTeams] = useState([]); // todos, sólo para contar equipos por club
  const [loading, setLoading] = useState(true);

  // Estado de filtros e interacción
  const [sportId, setSportId] = useState(''); // '' = Todos los deportes
  const [query, setQuery] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);

  // Carga inicial: sports + clubs + teams (todos, ligero, sólo para contar).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [sp, cl, tm] = await Promise.all([
          fetchSports(),
          fetchAdminList('clubs', {}),
          fetchAdminList('teams', {}),
        ]);
        if (cancelled) return;
        setSports(sp || []);
        setClubs(cl.items || []);
        setTeams(tm.items || []);
      } catch (err) {
        if (!cancelled) toast.error(friendlyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Mapas auxiliares
  const sportsById = useMemo(() => {
    const m = new Map();
    (sports || []).forEach((s) => m.set(s.id, s));
    return m;
  }, [sports]);

  const teamsCountByClub = useMemo(() => {
    const m = new Map();
    (teams || []).forEach((t) => {
      m.set(t.club_id, (m.get(t.club_id) || 0) + 1);
    });
    return m;
  }, [teams]);

  // Clubes filtrados por deporte y búsqueda por nombre.
  const visibleClubs = useMemo(() => {
    let list = clubs;
    if (sportId) {
      list = list.filter((c) => c.sport_id === sportId);
    }
    const term = query.trim().toLowerCase();
    if (term) {
      list = list.filter((c) => (c.name || '').toLowerCase().includes(term));
    }
    return list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [clubs, sportId, query]);

  // Cuando cambia el deporte, si el club seleccionado ya no pertenece → reset.
  useEffect(() => {
    if (!selectedClub || !sportId) return;
    if (selectedClub.sport_id !== sportId) setSelectedClub(null);
  }, [sportId, selectedClub]);

  const clubsOptions = clubs.map((c) => ({ value: c.id, label: c.name }));
  const sportsOptions = sports.map((s) => ({ value: s.id, label: s.name }));

  // ------------------------- VISTA 2: equipos de un club -------------------------
  if (selectedClub) {
    return (
      <AdminCrudShell
        title={`Equipos · ${selectedClub.name}`}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedClub(null)}
              data-testid="admin-teams-back-to-clubs"
              className="h-7 px-2 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/10"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Volver a clubes
            </Button>
            <span className="text-[var(--ns-text-muted)]">
              Deporte: <strong className="text-[var(--ns-text)]">{sportsById.get(selectedClub.sport_id)?.name || '—'}</strong>
            </span>
          </span>
        }
        entity="teams"
        testIdPrefix="admin-teams"
        searchPlaceholder="Buscar equipo…"
        extraQueryParams={{ club_id: selectedClub.id }}
        rowActions={TeamRowAction}
        initialForm={{
          club_id: selectedClub.id,
          sport_id: selectedClub.sport_id || '',
          name: '', category: 'senior',
          season: '2024-2025', gender: '', status: 'active',
        }}
        columns={[
          { key: 'name', label: 'Equipo', render: (r) => <span className="font-medium text-[var(--ns-text)]">{r.name}</span> },
          { key: 'sport_id', label: 'Deporte', render: (r) => sportsById.get(r.sport_id || selectedClub.sport_id)?.name || '—' },
          { key: 'category', label: 'Categoría', render: (r) => <span className="capitalize">{r.category}</span> },
          { key: 'gender', label: 'Género', render: (r) => <span className="capitalize">{genderLabel(r.gender)}</span> },
          { key: 'season', label: 'Temporada' },
          { key: 'status', label: 'Estado', render: (r) => <StatusBadge status={r.status} /> },
        ]}
        formFields={[
          { name: 'club_id', label: 'Club', type: 'select', required: true, options: clubsOptions },
          { name: 'sport_id', label: 'Deporte', type: 'select', options: sportsOptions, hint: 'Si lo dejas vacío, se hereda del club.' },
          { name: 'name', label: 'Nombre', required: true, placeholder: 'CD Ejemplo - Senior' },
          { name: 'category', label: 'Categoría', type: 'select', required: true, options: CATEGORY_OPTIONS },
          { name: 'season', label: 'Temporada', placeholder: '2024-2025' },
          { name: 'gender', label: 'Género', type: 'select', options: GENDER_OPTIONS },
          { name: 'status', label: 'Estado', type: 'select', required: true, options: STATUS_OPTIONS },
        ]}
      />
    );
  }

  // ------------------------- VISTA 1: lista de clubes -------------------------
  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <header className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold">
            Gestión admin
          </p>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl text-[var(--ns-text)] leading-tight">
            Equipos / Categorías
          </h1>
          <p className="mt-2 text-[var(--ns-text-muted)] text-sm">
            Selecciona un club para gestionar sus equipos. Filtra por deporte y busca por nombre de club.
          </p>
        </header>

        {/* Toolbar: filtro deporte + búsqueda */}
        <Card className="p-4 mb-4 border-[var(--ns-border)]">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-4">
              <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">
                Deporte
              </Label>
              <Select
                value={sportId || '__all__'}
                onValueChange={(v) => setSportId(v === '__all__' ? '' : v)}
              >
                <SelectTrigger
                  className="h-10 mt-1.5 rounded-xl border-[var(--ns-border)] bg-white"
                  data-testid="admin-teams-sport-filter"
                >
                  <SelectValue placeholder="Todos los deportes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los deportes</SelectItem>
                  {sports.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-8">
              <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">
                Buscar club
              </Label>
              <div className="relative mt-1.5">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ns-text-subtle)]" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Escribe el nombre del club…"
                  className="h-10 pl-9 pr-10 rounded-xl border-[var(--ns-border)] bg-white"
                  data-testid="admin-teams-club-search"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-md text-[var(--ns-text-subtle)] hover:text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]"
                    aria-label="Limpiar búsqueda"
                    data-testid="admin-teams-club-search-clear"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </Card>

        {/* Listado de clubes */}
        <Card className="border-[var(--ns-border)] overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-3 border-b border-[var(--ns-border)] bg-[var(--ns-bg-2)]/40 text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">
            <div className="col-span-6">Club</div>
            <div className="col-span-4">Deporte</div>
            <div className="col-span-2 text-right">Nº equipos</div>
          </div>

          {loading ? (
            <div className="p-4 space-y-3" data-testid="admin-teams-clubs-loading">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))}
            </div>
          ) : visibleClubs.length === 0 ? (
            <div
              className="p-10 text-center text-[var(--ns-text-muted)]"
              data-testid="admin-teams-clubs-empty"
            >
              <Users className="h-8 w-8 mx-auto mb-2 text-[var(--ns-text-subtle)]" />
              No hay clubes que coincidan con el filtro.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--ns-border)]" data-testid="admin-teams-clubs-list">
              {visibleClubs.map((c) => {
                const sport = sportsById.get(c.sport_id);
                const count = teamsCountByClub.get(c.id) || 0;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedClub(c)}
                      data-testid={`admin-teams-club-row-${c.id}`}
                      className="w-full text-left grid grid-cols-12 items-center px-4 py-3 hover:bg-[var(--ns-gold)]/5 focus-visible:bg-[var(--ns-gold)]/10 focus-visible:outline-none transition-colors"
                    >
                      <div className="col-span-6 flex items-center gap-2 min-w-0">
                        <span className="font-medium text-[var(--ns-text)] truncate">{c.name}</span>
                        <ChevronRight className="h-4 w-4 text-[var(--ns-text-subtle)] shrink-0" />
                      </div>
                      <div className="col-span-4 text-sm text-[var(--ns-text-muted)]">
                        {sport?.name || '—'}
                      </div>
                      <div
                        className="col-span-2 text-right text-sm font-semibold text-[var(--ns-text)]"
                        data-testid={`admin-teams-club-count-${c.id}`}
                      >
                        {count}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <p className="mt-3 text-xs text-[var(--ns-text-subtle)]" data-testid="admin-teams-clubs-results">
          {loading ? 'Cargando…' : `${visibleClubs.length} club${visibleClubs.length === 1 ? '' : 'es'}`}
        </p>
      </div>
    </AdminLayout>
  );
}
